import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClientError } from "@victor/api-client";
import type {
  SuperAdminResetPasswordResponse,
  SuperAdminTenantUser,
} from "@victor/types";
import { api } from "../../../lib/api";
import { Button } from "../../../ui/Button";
import { Modal } from "../../../ui/Modal";
import { PageSpinner } from "../../../ui/Spinner";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { Badge, EmptyInline, Panel, fmtDate, initials } from "./_ui";

type Props = { tenantId: string };

export function UsersTab({ tenantId }: Props) {
  const queryClient = useQueryClient();
  const [resetTarget, setResetTarget] = useState<SuperAdminTenantUser | null>(
    null,
  );
  const [resetResult, setResetResult] =
    useState<SuperAdminResetPasswordResponse | null>(null);
  const [impersonateTarget, setImpersonateTarget] =
    useState<SuperAdminTenantUser | null>(null);

  const usersQ = useQuery({
    queryKey: ["tenant-users", tenantId],
    queryFn: () => api.users.list(tenantId),
  });

  const statutMut = useMutation({
    mutationFn: ({
      userId,
      statut,
    }: {
      userId: string;
      statut: "actif" | "suspendu";
    }) => api.users.setStatut(tenantId, userId, statut),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["tenant-users", tenantId] }),
  });

  const resetMut = useMutation({
    mutationFn: (userId: string) => api.users.resetPassword(tenantId, userId),
    onSuccess: (r) => setResetResult(r),
  });

  const impersonateMut = useMutation({
    mutationFn: (userId: string) => api.users.impersonate(tenantId, userId),
    onSuccess: (r) => {
      window.open(r.redirectUrl, "_blank", "noopener,noreferrer");
      setImpersonateTarget(null);
    },
  });

  if (usersQ.isLoading) return <PageSpinner />;

  const users = usersQ.data ?? [];

  return (
    <div className="space-y-5">
      <Panel accent="info">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-50 text-accent-600 [&>svg]:h-5 [&>svg]:w-5">
            {ICON.users}
          </span>
          <p className="text-sm text-navy-600">
            Liste des utilisateurs du tenant. Le bouton{" "}
            <strong className="text-navy-800">Connexion</strong> ouvre une
            session impersonée (TTL 30 min) sur platform.myvictor.fr, tracée en
            audit.
          </p>
        </div>
      </Panel>

      <Panel padded={false}>
        <h3 className="flex items-center gap-2 px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-navy-500">
          <span className="text-navy-400">{ICON.users}</span>
          Utilisateurs
        </h3>
        {users.length === 0 ? (
          <div className="p-5">
            <EmptyInline>Aucun utilisateur.</EmptyInline>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-navy-100 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                <th className="px-5 py-2.5 font-bold">Utilisateur</th>
                <th className="px-5 py-2.5 font-bold">Rôles</th>
                <th className="px-5 py-2.5 font-bold">Statut</th>
                <th className="px-5 py-2.5 font-bold">MFA</th>
                <th className="px-5 py-2.5 font-bold">Dernière connexion</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {users.map((u) => (
                <tr key={u.id} className="transition-colors hover:bg-navy-50/40">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-xs font-black text-accent-700">
                        {initials(`${u.prenom} ${u.nom}`)}
                      </span>
                      <div className="min-w-0">
                        <p className="font-bold text-navy-800">
                          {u.prenom} {u.nom}
                        </p>
                        <p className="truncate text-xs text-navy-500">
                          {u.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <Badge key={r} tone="navy">
                          {r}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <Badge
                      tone={u.statut === "actif" ? "emerald" : "amber"}
                      dot
                    >
                      {u.statut}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    {u.mfaActive ? (
                      <span className="text-xs font-semibold text-emerald-700">
                        Oui
                      </span>
                    ) : (
                      <span className="text-xs text-navy-400">Non</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-navy-500 whitespace-nowrap">
                    {u.derniereConnexion ? fmtDate(u.derniereConnexion) : "-"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        onClick={() => setImpersonateTarget(u)}
                        disabled={u.statut !== "actif"}
                      >
                        Connexion
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setResetTarget(u)}
                      >
                        Reset MDP
                      </Button>
                      {u.statut === "actif" ? (
                        <Button
                          variant="ghost"
                          onClick={() =>
                            statutMut.mutate({
                              userId: u.id,
                              statut: "suspendu",
                            })
                          }
                        >
                          Suspendre
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          onClick={() =>
                            statutMut.mutate({
                              userId: u.id,
                              statut: "actif",
                            })
                          }
                        >
                          Réactiver
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <ConfirmDialog
        open={!!resetTarget && !resetResult}
        onClose={() => {
          setResetTarget(null);
          setResetResult(null);
        }}
        title="Réinitialiser le mot de passe ?"
        description={
          <p>
            Un nouveau mot de passe temporaire sera généré pour{" "}
            <strong>{resetTarget?.email}</strong>. Vous devrez le transmettre
            manuellement.
          </p>
        }
        confirmLabel="Générer un nouveau MDP"
        onConfirm={() => {
          if (resetTarget) resetMut.mutate(resetTarget.id);
        }}
        busy={resetMut.isPending}
      />

      {resetResult ? (
        <Modal
          open
          onClose={() => {
            setResetResult(null);
            setResetTarget(null);
          }}
          title="Mot de passe temporaire généré"
        >
          <div className="space-y-3">
            <div className="rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
                À transmettre maintenant
              </p>
              <code className="block rounded-lg bg-white px-3 py-2 text-sm font-mono text-navy-800 ring-1 ring-amber-200 break-all">
                {resetResult.temporaryPassword}
              </code>
              <Button
                variant="secondary"
                onClick={() =>
                  void navigator.clipboard.writeText(
                    resetResult.temporaryPassword,
                  )
                }
              >
                Copier
              </Button>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setResetResult(null);
                  setResetTarget(null);
                }}
              >
                J'ai noté
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      <ConfirmDialog
        open={!!impersonateTarget}
        onClose={() => setImpersonateTarget(null)}
        title="Ouvrir une session impersonée ?"
        description={
          <div className="space-y-2">
            <p>
              Vous allez ouvrir une session active{" "}
              <strong>
                en tant que {impersonateTarget?.prenom}{" "}
                {impersonateTarget?.nom}
              </strong>{" "}
              ({impersonateTarget?.email}) sur platform.myvictor.fr.
            </p>
            <p className="text-xs text-navy-500">
              TTL 30 minutes. Toutes vos actions seront imputées à cet
              utilisateur et tracées dans l'audit du tenant avec votre id de
              super-admin.
            </p>
          </div>
        }
        confirmLabel="Ouvrir la session"
        onConfirm={() => {
          if (impersonateTarget) impersonateMut.mutate(impersonateTarget.id);
        }}
        busy={impersonateMut.isPending}
      />

      {(statutMut.error || resetMut.error || impersonateMut.error) ? (
        <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {[statutMut.error, resetMut.error, impersonateMut.error]
            .filter(Boolean)
            .map((e) =>
              e instanceof ApiClientError ? e.message : "Erreur.",
            )
            .join(" · ")}
        </p>
      ) : null}
    </div>
  );
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  users: (
    <svg {...sp}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  ),
};
